import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  formatReactString,
  getMonthlyChoiceIdByRepeatType,
  getMonthlyRecurrenceChoices,
  getMonthlyRepeatType,
  MONTHLY_REPEAT_TYPE
} from "../utils/CommonUtils";

// Recurrence state + labels for the two create-meeting flows (RON-5827).
//
// Extracted from NewMeetingModal so BookMeeting can render the same controls
// without a second copy of the monthly logic. The module docs for both files
// already said the flows "must not drift apart" - this is what stops them:
// the rules below (which types carry an interval, what MonthlyRepeatType a row
// maps to, what the payload sends) now exist once.
//
// Deliberately NOT included: end date, the enable-repeat switch, and the
// recurrence dropdown's own item list. Those differ between the two screens -
// BookMeeting reads its options from QuickBookingInfo, NewMeetingModal from
// MeetingRoomListAndInfo, and each owns its own end-date reset - so they stay
// with the caller. This hook owns the recurrence *values* and their labels;
// each screen owns its own markup, because the layouts genuinely differ
// (offcanvas two-column form rows vs. the new-booking stacked form).

// Recurrence types that expose a "Repeat every N ..." interval, mapped to the
// unit label for that type. Biweekly (4) and Daily (1) are absent on purpose:
// biweekly already carries its own two-week interval, and daily has no design
// for one. Keys are the RepeatOption ids the API uses.
const REPEAT_INTERVAL_UNIT_KEY = {
  2: "edit_meeting_recurring_week_no_s",
  3: "edit_meeting_recurring_month_no_s"
};

// Monthly "On the <ordinal> <weekday>" labels, keyed by the ordinal that
// getMonthlyRecurrenceOptions returns (1-5, never higher). The _5 key reads
// "On the last %s" rather than "On the fifth %s", which is always accurate: a
// month spans at most 31 days = 4 weeks + 3 days, so a weekday occurs at most
// five times and the fifth is necessarily the final one.
const MONTHLY_ORDINAL_LABEL_KEY = {
  1: "edit_meeting_recurring_monthly_week_no_1",
  2: "edit_meeting_recurring_monthly_week_no_2",
  3: "edit_meeting_recurring_monthly_week_no_3",
  4: "edit_meeting_recurring_monthly_week_no_4",
  5: "edit_meeting_recurring_monthly_week_no_5"
};

const MONTHLY_CHOICE_IDS = ["day-of-month", "nth-weekday", "last-weekday"];

// "On the last <weekday>" shares the _5 key: a fifth occurrence can only ever
// be the final one, so one label serves both. That sharing is why a fifth
// occurrence drops the separate last-weekday row - both would render the same
// words - which getMonthlyRecurrenceChoices handles.
const MONTHLY_LAST_LABEL_KEY = "edit_meeting_recurring_monthly_week_no_5";

// RepeatOption ids that show the weekday chips. Weekly and Biweekly both pick
// days; every other type hides the row.
const WEEKDAY_CHIP_REPEAT_TYPES = [2, 4];

const MONTHLY_REPEAT_OPTION_ID = 3;

/**
 * Recurrence controls shared by NewMeetingModal and BookMeeting.
 *
 * @param {object} params
 * @param {Date} params.selectedDate the booking date; the monthly choices are a
 *        pure function of it, so moving from the 26th to the 12th turns "fourth
 *        Monday" into "second Monday" with no extra wiring at the call site.
 */
const useMeetingRecurrence = ({ selectedDate }) => {
  // useTranslation rather than the module-level i18n import: `defaultNS` is
  // "common" and it is the only namespace, so this resolves identically, and it
  // keeps the hook working under suites that mock react-i18next but stub
  // modules/i18n/i18n.js as an empty object.
  const { t, i18n } = useTranslation();
  const language = i18n?.language;

  const [repeatType, setRepeatType] = useState(0);
  // "Repeat every N" interval (1-99), shared by Weekly (2) and Monthly (3).
  // Sent as RepeatInterval since v5.1 (RONLARAVEL-21).
  const [repeatInterval, setRepeatInterval] = useState(1);
  const [weeklyRepeatOption, setWeeklyRepeatOption] = useState([]);
  // MonthlyRepeatType (v5.1): 0 Default | 1 Nth Day | 2 Nth Week | 3 Last Week.
  const [monthlyRepeatType, setMonthlyRepeatType] = useState(
    MONTHLY_REPEAT_TYPE.DEFAULT
  );
  // Which monthly row is selected. Still its own state, even though
  // MonthlyRepeatType now maps 1:1 to a row, so that a date change which removes
  // the selected row can fall back without clobbering the user's pick.
  const [monthlyChoiceId, setMonthlyChoiceId] = useState(null);

  // "Repeat every N Week(s)/Month(s)" options, 1-99, for the recurrence types
  // that carry an interval. The label is JSX so the count can be emphasised,
  // which is safe because the dropdown is rendered with searchable={false}
  // (its filter calls toString() on item[labelKey]). Split on %s rather than
  // replacing it, so locales that don't lead with the placeholder still render
  // the surrounding text in the right order. Built only for the active type, so
  // switching recurrence never pays for the list it isn't showing.
  const repeatIntervalOptions = useMemo(() => {
    const unitKey = REPEAT_INTERVAL_UNIT_KEY[repeatType];

    if (!unitKey) return [];

    const [before, after = ""] = t(unitKey).split("%s");

    return Array.from({ length: 99 }, (_, index) => {
      const count = index + 1;

      return {
        Value: count,
        Label: (
          <>
            {before}
            <span className="repeat-every-count">{count}</span>
            {after}
          </>
        )
      };
    });
    // `t` changes identity when the language does, so it covers the label text
    // on its own; `language` is only a dependency where Intl reads it directly
    // (monthlyRecurrenceChoices below).
  }, [t, repeatType]);

  // Monthly "repeat on" choices for the selected date. The set itself is date
  // driven (getMonthlyRecurrenceOptions), so it is recomputed whenever the user
  // picks a different date. Weekday names come from Intl rather than 70 more
  // locale keys; i18n.language is the 2-letter code Intl expects.
  const monthlyRecurrenceChoices = useMemo(() => {
    if (repeatType !== MONTHLY_REPEAT_OPTION_ID || !selectedDate) return [];

    const weekdayName = (weekday) => {
      // any Sunday-anchored week works - we only need the weekday's name
      const reference = new Date(Date.UTC(2024, 0, 7 + weekday));

      try {
        return new Intl.DateTimeFormat(language || "en", {
          weekday: "long",
          timeZone: "UTC"
        }).format(reference);
      } catch {
        return reference.toLocaleDateString("en", { weekday: "long" });
      }
    };

    // getMonthlyRecurrenceChoices already applies the fifth-occurrence collapse
    // (see CommonUtils); MONTHLY_CHOICE_IDS is the product-side gate on top.
    return getMonthlyRecurrenceChoices(selectedDate)
      .filter((option) => MONTHLY_CHOICE_IDS.includes(option.id))
      .map((option) => {
        let template;

        if (option.id === "day-of-month") {
          template = t("edit_meeting_recurring_monthly_day_no_s");
        } else if (option.id === "last-weekday") {
          template = t(MONTHLY_LAST_LABEL_KEY);
        } else {
          template = t(MONTHLY_ORDINAL_LABEL_KEY[option.ordinal] || "");
        }

        const value =
          option.id === "day-of-month"
            ? option.dayOfMonth
            : weekdayName(option.weekday);

        return {
          id: option.id,
          monthlyRepeatType: getMonthlyRepeatType(option.id),
          // formatReactString is the project's own %s renderer - it returns
          // React parts, so the placeholder can be highlighted in place.
          label: formatReactString(template, [value], {
            defaultRenderer: (part) => (
              <span className="monthly-choice-value">{part}</span>
            )
          })
        };
      });
  }, [t, language, repeatType, selectedDate]);

  // Which row is actually checked. Derived rather than stored so that a date
  // change which removes the selected row (e.g. 28 Sep -> 21 Sep drops "On the
  // last Monday") falls back on its own, with no effect and no extra render.
  const activeMonthlyChoiceId = useMemo(() => {
    if (
      monthlyChoiceId &&
      monthlyRecurrenceChoices.some((choice) => choice.id === monthlyChoiceId)
    ) {
      return monthlyChoiceId;
    }

    // MonthlyRepeatType maps straight back to a row, so an edited meeting opens
    // on the pattern it was saved with - including "last week of month", which
    // the old weekday-id value could not express. DEFAULT (not monthly, or a
    // pre-v5.1 meeting with nothing stored) falls back to day-of-month.
    const fromRepeatType = getMonthlyChoiceIdByRepeatType(monthlyRepeatType);

    if (
      fromRepeatType &&
      monthlyRecurrenceChoices.some((choice) => choice.id === fromRepeatType)
    ) {
      return fromRepeatType;
    }

    return "day-of-month";
  }, [monthlyChoiceId, monthlyRecurrenceChoices, monthlyRepeatType]);

  const showsWeekdayChips = WEEKDAY_CHIP_REPEAT_TYPES.includes(repeatType);

  /**
   * Switch recurrence type, applying the per-type resets.
   *
   * The interval is per recurrence type, so it always restarts at 1 rather than
   * carrying a stale count ("5 Week(s)") into the new type. Weekday chips are
   * cleared only when the new type has none, so switching Weekly <-> Biweekly
   * keeps the user's days.
   *
   * The caller still resets its own end date - that state is not owned here.
   */
  const changeRepeatType = (value) => {
    const repeatValue = Number(value);

    setRepeatType(repeatValue);
    setRepeatInterval(1);
    // let the monthly row fall back to whatever monthlyRepeatType ends up being
    setMonthlyChoiceId(null);

    if (repeatValue === MONTHLY_REPEAT_OPTION_ID) {
      setWeeklyRepeatOption([]);
      // default to "On day N" - NTH_DAY is the day-of-month row, and clearing
      // monthlyChoiceId above lets activeMonthlyChoiceId derive it
      setMonthlyRepeatType(MONTHLY_REPEAT_TYPE.NTH_DAY);
      return;
    }

    setMonthlyRepeatType(MONTHLY_REPEAT_TYPE.DEFAULT);

    if (!WEEKDAY_CHIP_REPEAT_TYPES.includes(repeatValue)) {
      setWeeklyRepeatOption([]);
    }
  };

  const toggleWeeklyDay = (dayValue) => {
    const value = String(dayValue);

    setWeeklyRepeatOption((prev) =>
      prev.includes(value)
        ? prev.filter((day) => day !== value)
        : [...prev, value]
    );
  };

  const isWeeklyDaySelected = (dayValue) =>
    weeklyRepeatOption.includes(String(dayValue));

  const selectMonthlyChoice = (choice) => {
    if (!choice) return;

    setMonthlyChoiceId(choice.id);
    setMonthlyRepeatType(choice.monthlyRepeatType);
    setWeeklyRepeatOption([]);
  };

  const resetRecurrence = () => {
    setRepeatType(0);
    setRepeatInterval(1);
    setWeeklyRepeatOption([]);
    setMonthlyRepeatType(MONTHLY_REPEAT_TYPE.DEFAULT);
    setMonthlyChoiceId(null);
  };

  /**
   * The four recurrence parameters CreateNewMeeting / EditMeeting expect (v5.1).
   *
   * Two deliberate behaviours:
   * - MonthlyRepeatType sends DEFAULT for any non-Monthly recurrence, so
   *   switching away from Monthly cannot leak the last pattern the user clicked.
   * - RepeatInterval sends 1 for Daily/Biweekly/No-repeat - only Weekly (2) and
   *   Monthly (3) expose the control, per REPEAT_INTERVAL_UNIT_KEY.
   */
  const getRecurrencePayload = () => ({
    RepeatOption: repeatType || 0,
    WeeklyRepeatOption:
      Array.isArray(weeklyRepeatOption) && weeklyRepeatOption.length > 0
        ? weeklyRepeatOption.join(",")
        : "0",
    MonthlyRepeatType:
      repeatType === MONTHLY_REPEAT_OPTION_ID
        ? monthlyRepeatType
        : MONTHLY_REPEAT_TYPE.DEFAULT,
    RepeatInterval:
      repeatType === 2 || repeatType === MONTHLY_REPEAT_OPTION_ID
        ? Number(repeatInterval) || 1
        : 1
  });

  return {
    // values
    repeatType,
    repeatInterval,
    weeklyRepeatOption,
    monthlyRepeatType,
    monthlyChoiceId,

    // setters, for prefilling an existing meeting (edit mode)
    setRepeatType,
    setRepeatInterval,
    setWeeklyRepeatOption,
    setMonthlyRepeatType,
    setMonthlyChoiceId,

    // derived
    repeatIntervalOptions,
    monthlyRecurrenceChoices,
    activeMonthlyChoiceId,
    showsWeekdayChips,

    // handlers
    changeRepeatType,
    toggleWeeklyDay,
    isWeeklyDaySelected,
    selectMonthlyChoice,
    resetRecurrence,

    // payload
    getRecurrencePayload
  };
};

export default useMeetingRecurrence;
